import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import {
  Colors,
  FontSize,
  Radii,
  Space,
  FontFamily,
  ChipTone,
} from '../theme/tokens';
import { Chip } from './Chip';
import type { ApiSlaState } from '../schema/tickets/ticket.schema';
import {
  classifySla,
  formatDuration,
  slaLabel,
  type SlaVisualState,
} from '../lib/sla';

// Re-exported so call sites can keep importing from the component. The logic
// lives in lib/sla.ts, free of react-native, so it can be tested on its own.
export { classifySla, formatDuration, slaLabel };
export type { SlaVisualState };

/**
 * SKTICK-DB-001 Surface D — SLA state.
 *
 * THE CLIENT COMPUTES NOTHING. `overdue` and `effectiveRemainingSeconds` come
 * resolved from the server (SKTICK-CR-001 WP-5), net of every pause. A second
 * calculation here would disagree with the escalation sweeper and with SK HQ
 * Console's overdue count, and then a console would contradict the ticket it
 * refers to.
 *
 * The timer pauses in `pending` and `on_hold`. A paused clock MUST look
 * paused — a countdown that silently is not counting reads as a stuck screen,
 * and the agent has no way to tell the difference.
 */

const TONE: Record<SlaVisualState, ChipTone> = {
  none: 'default',
  running: 'default',
  approaching: 'warn',
  paused: 'neutral',
  breached: 'danger',
};

interface SlaBadgeProps {
  sla: ApiSlaState | undefined;
  style?: StyleProp<ViewStyle>;
}

/** Compact form for a list row. Renders nothing when the ticket has no SLA. */
export function SlaBadge({ sla, style }: SlaBadgeProps) {
  const label = slaLabel(sla);
  if (!label) {
    return null;
  }
  const state = classifySla(sla);
  return (
    <Chip
      label={label}
      tone={TONE[state]}
      // The stopped clock is the one state that must be unmistakable at a
      // glance, so it carries a dot where the running states do not.
      dot={state === 'paused' || state === 'breached'}
      style={style}
    />
  );
}

interface SlaDetailProps {
  sla: ApiSlaState | undefined;
  /** Shown as "Reopened N times" when ≥ 1. */
  reopenCount?: number;
  style?: StyleProp<ViewStyle>;
}

/**
 * Detail form. Shows the paused fact explicitly rather than leaving the reader
 * to infer it from a number that is not moving.
 */
export function SlaDetail({ sla, reopenCount, style }: SlaDetailProps) {
  const state = classifySla(sla);
  const showReopened = typeof reopenCount === 'number' && reopenCount >= 1;
  if (state === 'none' && !showReopened) {
    return null;
  }

  return (
    <View style={[styles.detail, style]}>
      {state !== 'none' && sla && (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>Resolution SLA</Text>
            <SlaBadge sla={sla} />
          </View>

          {state === 'paused' && (
            // Say the clock is stopped in words. The badge alone reads as a
            // status; this says why nothing is moving.
            <Text style={styles.pausedNote}>
              The clock is stopped while this ticket waits on the requester.
            </Text>
          )}

          {sla.resolutionDueAt && (
            <View style={styles.row}>
              <Text style={styles.label}>Due</Text>
              <Text style={styles.value}>
                {new Date(sla.resolutionDueAt).toLocaleString()}
              </Text>
            </View>
          )}

          {sla.breachedAt && (
            <View style={styles.row}>
              <Text style={styles.label}>Breached</Text>
              <Text style={styles.value}>
                {new Date(sla.breachedAt).toLocaleString()}
              </Text>
            </View>
          )}

          {sla.pausedTotalSeconds > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Paused for</Text>
              <Text style={styles.value}>
                {formatDuration(sla.pausedTotalSeconds)}
              </Text>
            </View>
          )}
        </>
      )}

      {showReopened && (
        // The strongest signal on a ticket that something is not actually
        // fixed, so it sits with the SLA rather than buried in history.
        <View style={styles.row}>
          <Text style={styles.label}>History</Text>
          <Chip
            label={`Reopened ${reopenCount} ${
              reopenCount === 1 ? 'time' : 'times'
            }`}
            tone="warn"
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  detail: {
    gap: Space[2],
    padding: Space[3],
    backgroundColor: Colors.paper2,
    borderRadius: Radii.r3,
    borderWidth: 1,
    borderColor: Colors.line,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Space[3],
  },
  label: {
    fontSize: FontSize.sm,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
  },
  value: {
    fontSize: FontSize.sm,
    color: Colors.ink,
    fontFamily: FontFamily.mono,
  },
  pausedNote: {
    fontSize: FontSize.sm,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
    lineHeight: 18,
  },
});
